import type { Residence } from '@repo/sdk/gen'
import { Badge } from '@repo/ui/badge'
import { ButtonLink } from '@repo/ui/components/button-link'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/components/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/components/data-table/types'
import { ListInputSearch } from '@repo/ui/components/list-input-search'
import { ListLayout } from '@repo/ui/components/list-layout'
import { useQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	Link,
	stripSearchParams,
	useNavigate
} from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { CallStateBadge } from '@/routes/(private)/-components/call-state-badge'
import { usePageSize } from '@/stores/use-page-size'
import { DsuRateDialog } from './-components/dsu-rate-dialog'
import { residencesListQueryOptions } from './-query/query-options'

const EMPTY_RESIDENCES: Residence[] = []

const searchSchema = z.object({
	search: z.string().trim().default(''),
	page: z.number().int().min(1).default(1)
})

export const Route = createFileRoute('/(private)/residences/')({
	component: AdminResidences,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	}
})

function AdminResidences() {
	const { t } = useTranslation()
	const navigate = useNavigate({ from: Route.fullPath })
	const params = Route.useSearch()
	const pageSize = usePageSize((s) => s.pageSize)
	const setPageSize = usePageSize((s) => s.setPageSize)

	const columns: DataTableColumn<Residence>[] = [
		{
			id: 'name',
			header: t('admin-residences:fields.name'),
			pinned: 'left',
			cell: (residence) => (
				<Link
					className='block truncate hover:underline'
					params={{ residenceId: residence.id }}
					title={residence.name}
					to='/residences/$residenceId'
				>
					{residence.name}
				</Link>
			),
			minWidth: 240,
			flex: 2
		},
		{
			id: 'city',
			header: t('admin-residences:columns.city'),
			cell: (residence) => (
				<span>
					{residence.sites[0]?.city}
					{residence.sites.length > 1 && (
						<span className='ml-1.5 text-muted-foreground text-xs'>
							{t('admin-residences:columns.more_sites', {
								count: residence.sites.length - 1
							})}
						</span>
					)}
				</span>
			),
			minWidth: 140,
			flex: 1
		},
		{
			id: 'seats',
			header: t('admin-residences:columns.seats'),
			cell: (residence) => (
				<span className='tabular-nums'>
					{residence.q30SingleBeds +
						residence.q30DoubleBeds +
						residence.q70SingleBeds +
						residence.q70DoubleBeds}
				</span>
			),
			minWidth: 90,
			flex: 1
		},
		{
			id: 'call',
			header: t('admin-residences:columns.call'),
			cell: (residence) => (
				<div className='flex flex-col items-start gap-1'>
					<div className='flex items-center gap-2'>
						<CallStateBadge state={residence.call.state} />
						<span className='text-muted-foreground text-xs tabular-nums'>
							{residence.call.academicYear}
						</span>
					</div>
					{residence.call.nextAcademicYear && (
						<span className='text-muted-foreground text-xs'>
							{t('admin-residences:columns.next_year_ready', {
								year: residence.call.nextAcademicYear
							})}
						</span>
					)}
					{residence.deactivatedAt && (
						<Badge variant='neutral'>
							{t('admin-residences:deactivated.badge')}
						</Badge>
					)}
				</div>
			),
			minWidth: 180,
			flex: 1
		}
	]
	const columnsState = useDataTableColumnState(columns)

	const query = useQuery(
		residencesListQueryOptions({
			page: params.page,
			limit: pageSize,
			filters: params.search ? { search: params.search } : undefined
		})
	)

	return (
		<ListLayout
			controlsSlot={
				<ListInputSearch
					placeholder={t('admin-residences:search_placeholder')}
					resetKeys={['page']}
				/>
			}
			headEndSlot={
				<>
					<DsuRateDialog />
					<ButtonLink size='sm' to='/residences/new'>
						<Plus />
						{t('admin-residences:new.button')}
					</ButtonLink>
				</>
			}
			listSlot={
				<DataTable
					columns={columns}
					columnsState={columnsState}
					data={query.data?.data ?? EMPTY_RESIDENCES}
					onRowClick={(row) =>
						navigate({
							params: { residenceId: row.id },
							to: '/residences/$residenceId'
						})
					}
					pagination={{
						pageIndex: params.page - 1,
						pageCount: query.data?.meta.totalPages ?? 1,
						onPageChange: (next) =>
							navigate({
								search: (prev) => ({ ...prev, page: next + 1 }),
								replace: true
							}),
						pageSize,
						onPageSizeChange: (next) => {
							setPageSize(next)
							navigate({
								search: (prev) => ({ ...prev, page: 1 }),
								replace: true
							})
						}
					}}
					state={{
						loading: query.isPending || query.isPlaceholderData,
						error: query.isError,
						empty: t('admin-residences:empty')
					}}
				/>
			}
			title={t('admin-residences:page_title')}
		/>
	)
}
